'use client';

import { useEffect, useRef } from 'react';
import ReactDOMServer from 'react-dom/server';
import { LocationMapPin } from '@icons';
import './_location-map.scss';
import 'mapbox-gl/dist/mapbox-gl.css';
import mapboxgl from 'mapbox-gl';
import Button from '@commons/Button/Button';
import useDictionary from '@modules/Dictionary/useDictionary';
import { useSiteConfig } from '@commons/SiteConfig';

const defaultLocations = [];
const defaultCenterLocation = { lat: 59.58188095567466, long: 8.535365846655854 };

export default function MultiLocationMap({
  locations = defaultLocations,
  centerLocation = defaultCenterLocation,
  address = ''
}) {
  const { dictionary } = useDictionary();
  const { MAPBOX_ACCESS_TOKEN } = useSiteConfig();

  const mapLabel = dictionary?.map?.map || 'Kart';
  const mapRef = useRef(null);
  const mapContainer = useRef(null);

  useEffect(() => {
    // Samma villkor som returnen nedan: hooken måste köras varje render,
    // men kartan ska inte byggas utan platser.
    if (!locations || !locations.length) return;

    mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;
    mapRef.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/visittelemark/cmmamxet5006201scdjrn3iku',
      center: [centerLocation.long, centerLocation.lat], // centering on Telemark by default
      zoom: 6,
      locale: {
        'Map.Title': mapLabel,
        'AttributionControl.ToggleAttribution': dictionary?.map?.toggleAttribution || 'Vis kildeinformasjon',
        'AttributionControl.MapFeedback': dictionary?.map?.mapFeedback || 'Tilbakemelding på kartet'
      }
    });

    if (mapContainer.current && mapboxgl) {
      locations.forEach(({ lat, long, name }) => {
        if (lat && long) {
          const markerElement = document.createElement('div');
          markerElement.className = 'marker';
          const pinHtml = ReactDOMServer.renderToStaticMarkup(<LocationMapPin className="LocationMap__pin" />);
          markerElement.innerHTML = pinHtml;
          new mapboxgl.Marker(markerElement).setLngLat([long, lat]).addTo(mapRef.current);
        }
      });
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
      }
    };
  }, [locations, centerLocation]);

  // Efter hookarna: en tidig return före dem bryter mot rules-of-hooks.
  if (!locations || !locations.length) return null;

  return (
    <section className="LocationMap">
      <div
        className="LocationMap__map-view"
        data-lat={centerLocation.lat}
        data-lng={centerLocation.long}
        role="region"
        aria-label={mapLabel}
        ref={mapContainer}
      />
      <div className="LocationMap__info">
        {address && (
          <div className="LocationMap__address">
            <p className="LocationMap__address-line">{address}</p>
          </div>
        )}
        {locations.length === 1 && (
          <div className="LocationMap__location">
            <p className="LocationMap__location-lat">
              {centerLocation.lat}, {centerLocation.long}
            </p>
          </div>
        )}
        <div className="LocationMap__explore">
          <Button priority="secondary" href="/map">
            {dictionary?.map?.openExplorer || 'open in the map explorer'}
          </Button>
        </div>
      </div>
    </section>
  );
}
